/**
 * VoiceBubble Component
 * Floating bubble UI for voice interaction
 */

import React from 'react';
import clsx from 'clsx';
import type { VoiceState } from '@ai360smartsearch/shared';
import { AudioWaveform } from './AudioWaveform';
import './VoiceBubble.css';

interface VoiceBubbleProps {
  voiceState: VoiceState;
  position?: 'bottom-right' | 'bottom-left' | 'bottom-center';
  onClick: () => void;
}

export const VoiceBubble: React.FC<VoiceBubbleProps> = ({
  voiceState,
  position = 'bottom-right',
  onClick,
}) => {
  const getStateColor = () => {
    switch (voiceState) {
      case 'listening':
        return 'var(--ai360-voice-listening)';
      case 'processing':
        return 'var(--ai360-voice-processing)';
      case 'speaking':
        return 'var(--ai360-voice-speaking)';
      default:
        return 'var(--ai360-voice-idle)';
    }
  };

  const getStateIcon = () => {
    switch (voiceState) {
      case 'listening':
        return <AudioWaveform isActive={true} />;
      case 'processing':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        );
      case 'speaking':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"
              fill="currentColor"
            />
            <path d="M19 10v2a7 7 0 1 1-14 0v-2" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path d="M19 10v2a7 7 0 1 1-14 0v-2" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'ai360-voice-bubble',
        `ai360-voice-bubble--${position}`,
        `ai360-voice-bubble--${voiceState}`
      )}
      style={{ backgroundColor: getStateColor() }}
      aria-label={`Voice ${voiceState}`}
    >
      <div className="ai360-voice-bubble-icon">
        {getStateIcon()}
      </div>
      
      {voiceState !== 'idle' && (
        <div className="ai360-voice-bubble-pulse" />
      )}
    </button>
  );
};
